import Button from 'react-bootstrap/Button';
import ReactSlider from 'react-slider';
import styled from 'styled-components';

import PreviewComponent from './PreviewComponent';
import MintPriceComponent from './MintPriceComponent';
import TokenCountComponent from './TokenCountComponent';
import MintButton from './MintButton';
import { rgbToHue } from '../utils/Colour';

const StyledSlider = styled(ReactSlider)`
      width: 100%;
      height: 20px;
  `;

const StyledThumb = styled.div`
      height: 20px;
      line-height: 20px;
      width: 20px;
      text-align: center;
      background-color: #777;
      color: #fff;
      font-size: 10px;
      border-radius: 50%;
      cursor: grab;
  `;

// react-slider hands `key` back inside the props object; React 19 warns if that
// is spread into JSX, so pull it out and pass it directly.
const Thumb = ({ key, ...props }: any, state: any) => <StyledThumb key={key} {...props}>{state.valueNow}</StyledThumb>;

const StyledOneThumbTrack = styled.div`
    top: 0;
    bottom: 0;
    background: #ddd;
    border-radius: 999px;
  `;
const OneThumbTrack = ({ key, ...props }: any) => <StyledOneThumbTrack key={key} {...props} />;

// styled-components v6 forwards unknown props to the DOM, so the index has to
// be transient ($-prefixed) to stay out of the rendered markup.
const StyledTwoThumbTrack = styled.div<{ $index: number }>`
      top: 0;
      bottom: 0;
      background: ${props => (props.$index === 1 ? '#aaa' : '#ddd')};
      border-radius: 999px;
  `;
const TwoThumbTrack = ({ key, ...props }: any, state: any) => <StyledTwoThumbTrack key={key} {...props} $index={state.index} />;

// The colour pickers used to come from react-color, which is unmaintained and
// breaks under React 19 (it leans on defaultProps for function components).
// They are now the same ReactSlider as every other control, over a gradient.
const GradientBar = styled.div`
      border-radius: 999px;
      overflow: hidden;
  `;
const TransparentTrack = ({ key, ...props }: any) => <div key={key} {...props} />;

const HUE_GRADIENT =
  'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)';
const CHECKERBOARD =
  'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 0 / 12px 12px';

const MintOptions = (props: any) => {

  const { r, g, b, a } = props.tintColour;
  const hue = rgbToHue({ r, g, b });
  const alphaPercent = Math.round(a * 100);

  const AlphaThumb = ({ key, ...thumbProps }: any) => <StyledThumb key={key} {...thumbProps}>{alphaPercent}</StyledThumb>;

  return (
    <div>
      Random values will be chosen for each stripe within the range you choose!

      <div className="mintRow">
        <PreviewComponent className="preview" svg={props.svg} />

        <div className="mintInputs">
          <div className="mintInput">
            Zoom (closer - farther) <br />

            <StyledSlider
              value={props.zoom}
              renderTrack={OneThumbTrack}
              renderThumb={Thumb}
              min={0}
              max={100}
              onAfterChange={props.zoomChanged}
            />
          </div>

          <div className="mintInput">
            Rotation Degrees<br />

            <StyledSlider
              value={props.rotationDegrees}
              renderTrack={OneThumbTrack}
              renderThumb={Thumb}
              min={0}
              max={360}
              step={1}
              onAfterChange={props.rotationDegreesChanged}
            />
          </div>

          <div className="mintInput">
            Rotation Range<br />

            <StyledSlider
              value={props.rotationRange}
              renderTrack={OneThumbTrack}
              renderThumb={Thumb}
              min={0}
              max={180}
              step={1}
              onAfterChange={props.rotationRangeChanged}
            />
          </div>

          <div className="mintInput">
            Stripe width (thinner - fatter) <br />

            <StyledSlider
              value={props.widthRange}
              renderTrack={TwoThumbTrack}
              renderThumb={Thumb}
              min={25}
              max={250}
              onAfterChange={props.widthRangeChanged}
            />
          </div>

          <div className="mintInput">
            Animation speed (faster - slower)<br />
            <StyledSlider
              value={props.speedRange}
              renderTrack={TwoThumbTrack}
              renderThumb={Thumb}
              min={25}
              max={250}
              onAfterChange={props.speedRangeChanged}
            />
          </div>

          <div className="mintInput">
            Tint colour<br />
            <GradientBar style={{ background: HUE_GRADIENT }}>
              <StyledSlider
                className="mintPicker"
                value={hue}
                renderTrack={TransparentTrack}
                renderThumb={Thumb}
                min={0}
                max={359}
                step={1}
                ariaLabel="Tint hue"
                onAfterChange={props.tintColourChanged}
              />
            </GradientBar>
          </div>

          <div className="mintInput">
            Tint % <br />
            <GradientBar
              style={{
                background: `linear-gradient(to right, rgba(${r}, ${g}, ${b}, 0), rgb(${r}, ${g}, ${b})), ${CHECKERBOARD}`,
              }}
            >
              <StyledSlider
                className="mintPicker"
                value={alphaPercent}
                renderTrack={TransparentTrack}
                renderThumb={AlphaThumb}
                min={0}
                max={100}
                step={1}
                ariaLabel="Tint percentage"
                onAfterChange={props.tintAlphaChanged}
              />
            </GradientBar>
          </div>

        </div>
      </div>
      <div className="textTraits">{props.traits}</div>
      <Button variant="primary" onClick={props.refreshSeed}>New seed</Button>
      <Button variant="primary" onClick={props.refreshParams}>Randomize params</Button>
      <MintButton mint={props.mint} disabled={props.isCollectionLoading} />

      <MintPriceComponent mintPrice={props.mintPrice} formattedMintPrice={props.formattedMintPrice} loading={props.isCollectionLoading} />
      <TokenCountComponent tokenCount={props.tokenCount} tokenLimit={props.tokenLimit} loading={props.isCollectionLoading} />
    </div>
  );
}

export default MintOptions;
